/** @format */

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
	X,
	Filter as FilterIcon,
	Palette,
	Layers,
	Percent,
	HelpCircle,
	Info,
	ShoppingCart,
	Trash2,
	Check,
} from "lucide-react"; // Added more icons
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";

// Components (Ensure paths are correct)
import FabricSeriesHeader from "../components/FabricSeriesHeader";
import ImageZoomWithButton from "../components/button/ImageZoomWithButton";
import { LoadingSpinner } from "../components/LoadingSpinner";

// Actions (Ensure paths are correct)
import { filterFabricsByGroup, groupFabric } from "../actions/blindActions";
import { addToSwatchCart, removeFromSwatchCart } from "../actions/cartActions";
import { Toast } from "./customize/Toast";

// --- Helpers ---
const normalizeImagePath = (imgPath, colorName = "") => {
	// ... (same function as before)
	if (!imgPath) return "";
	let processedImg = imgPath.replace("image/", "/images/");
	if (!/\.(jpg|jpeg|png|webp)$/i.test(processedImg) && colorName) {
		processedImg += `${colorName}.jpg`;
	}
	return processedImg.toLowerCase();
};

const SHADE_LIMIT = 5; // Define sample limit as a constant

// --- Main Component ---
const FabricSwatch = () => {
	const dispatch = useDispatch();
	const fabricCart = useSelector(
		(state) => state.swatchCart?.swatchCartItems || []
	);
	const isMobile = useMediaQuery({ maxWidth: 768 });

	// --- State ---
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [showFilters, setShowFilters] = useState(!isMobile);
	const [toast, setToast] = useState(null);

	// Filters
	const [selectedShade, setSelectedShade] = useState("roller");
	const [selectedGroup, setSelectedGroup] = useState("All"); // Transparency
	const [selectedColor, setSelectedColor] = useState("All"); // Color Family
	const [selectedPenetration, setSelectedPenetration] = useState("All"); // Sunscreen Openness

	// Data
	const [allRawFabrics, setAllRawFabrics] = useState([]);
	const [allGroups, setAllGroups] = useState([]);
	const [allColours, setAllColours] = useState([]);

	// --- Data Fetching ---
	const loadFabricDataForShade = useCallback(async (shade) => {
		// ... (same data fetching logic as before - fetches all, processes, sets raw data states) ...
		setLoading(true);
		setError(null);
		setAllRawFabrics([]);
		setAllGroups([]);
		setAllColours([]);
		const shadeParam = `${shade} shade`;
		const widthParam = 24;
		try {
			const groupsResult = await groupFabric(widthParam, shadeParam);
			const validGroups = groupsResult
				.map((g) => g.charAt(0).toUpperCase() + g.slice(1))
				.filter((g) => g !== "Null" && g !== "Sustainable");
			setAllGroups(validGroups);
			const fabricsResult = await filterFabricsByGroup(
				widthParam,
				shadeParam,
				"All",
				"All"
			);
			let processedFabrics = [];
			let uniqueColorFamilies = new Set();
			fabricsResult.seriesColourImage.forEach((item) => {
				(item.image || []).forEach((imgPath, i) => {
					let penetrationValue = item.light_penetration?.[i] || null;
					if (penetrationValue) {
						penetrationValue = String(penetrationValue);
						if (penetrationValue === "0.01") penetrationValue = "1%";
						else if (penetrationValue === "0.03") penetrationValue = "3%";
						else if (penetrationValue === "0.05") penetrationValue = "5%";
						else if (penetrationValue === "0.08") penetrationValue = "8%";
					}
					const fabricData = {
						code: item.code?.[i] || null,
						colour: item.colour?.[i] || "N/A",
						colourFamily: item.colourFamily?.[i] || "Other",
						group: item.group?.[i] || "Unknown",
						image: normalizeImagePath(imgPath, item.colour?.[i]),
						series: item.series?.[i] || "Unknown Series",
						priceGroup: item.priceGroup || 0,
						penetration: penetrationValue,
						composition: item.composition?.[i] || "-",
						fire_rating: item.fire_rating?.[i] || "-",
						green_rating: item.green_rating?.[i] || "-",
						antimicrobial_rating: item.antimicrobial_rating?.[i] || "-",
						ocs_name:
							item.ocs_name?.[i] || item.series?.[i] || "Unknown Series",
						ocs_colour_name:
							item.ocs_colour_name?.[i] || item.colour?.[i] || "N/A",
					};
					if (fabricData.code && fabricData.image) {
						processedFabrics.push(fabricData);
						uniqueColorFamilies.add(fabricData.colourFamily);
					}
				});
			});
			setAllRawFabrics(processedFabrics);
			setAllColours(["All", ...Array.from(uniqueColorFamilies).sort()]);
		} catch (err) {
			console.error(`Error loading fabric data for ${shade}:`, err);
			setError(`Failed to load fabrics for ${shade}. Please try again.`);
			setAllRawFabrics([]);
			setAllGroups([]);
			setAllColours([]);
		} finally {
			setLoading(false);
		}
	}, []);

	// --- Filtered & Grouped Data ---
	const groupedFilteredFabrics = useMemo(() => {
		// ... (same filtering and grouping logic as before) ...
		if (!allRawFabrics || allRawFabrics.length === 0) return {};
		const filtered = allRawFabrics.filter((item) => {
			const groupMatch =
				selectedGroup === "All" ||
				item.group?.toLowerCase() === selectedGroup.toLowerCase();
			const colorMatch =
				selectedColor === "All" ||
				item.colourFamily?.toLowerCase() === selectedColor.toLowerCase();
			const penetrationMatch =
				selectedGroup !== "Sunscreen" ||
				selectedPenetration === "All" ||
				item.penetration === selectedPenetration;
			return groupMatch && colorMatch && penetrationMatch;
		});
		const grouped = filtered.reduce((acc, item) => {
			const seriesName = item.ocs_name || "Unknown Series";
			if (!acc[seriesName]) acc[seriesName] = [];
			acc[seriesName].push(item);
			return acc;
		}, {});
		Object.keys(grouped).forEach((series) => {
			grouped[series].sort(
				(a, b) => a.ocs_colour_name?.localeCompare(b.ocs_colour_name || "") || 0
			);
		});
		return grouped;
	}, [allRawFabrics, selectedGroup, selectedColor, selectedPenetration]);

	// --- useEffect Hooks ---
	useEffect(() => {
		loadFabricDataForShade(selectedShade);
	}, [selectedShade, loadFabricDataForShade]);
	useEffect(() => {
		if (!isMobile && !showFilters) setShowFilters(true);
	}, [isMobile]);

	// --- Cart Handlers ---
	const handleAddToCart = (item) => {
		if (fabricCart.length >= SHADE_LIMIT) {
			setToast({
				message: `Maximum ${SHADE_LIMIT} samples allowed. Please remove one first.`,
				type: "error",
			});
			return;
		}
		if (!item?.code || !item?.series || !item?.colour) {
			/* ... error handling ... */ return;
		}
		const fabricItem = {
			shade: `${selectedShade.charAt(0).toUpperCase()}${selectedShade.slice(
				1
			)} Shade`,
			name: `${item.series} ${item.colour}`,
			image: item.image,
			code: item.code,
			type: item.series,
			series: item.series,
			ocs_name: item.ocs_name,
			ocs_colour_name: item.ocs_colour_name,
		};
		dispatch(addToSwatchCart({ item: fabricItem }));
		setToast({
			message: `Sample "${item.ocs_colour_name || item.name}" added.`,
			type: "success",
		});
	};

	const handleRemoveFromCart = (itemToRemove) => {
		/* ... same remove logic ... */
		const cartItemToRemove = fabricCart.find(
			(cartItem) =>
				cartItem.item.code === itemToRemove.code &&
				cartItem.item.shade.toLowerCase().startsWith(selectedShade)
		);
		if (cartItemToRemove) {
			dispatch(removeFromSwatchCart(cartItemToRemove));
			setToast({
				message: `Sample "${
					itemToRemove.ocs_colour_name || itemToRemove.colour
				}" removed.`,
				type: "success",
			});
		} else {
			console.warn("Could not find item to remove in cart:", itemToRemove);
		}
	};

	const existsInCart = (code) => {
		/* ... same exists logic ... */
		const currentShadeName = `${selectedShade
			.charAt(0)
			.toUpperCase()}${selectedShade.slice(1)} Shade`;
		return fabricCart.some(
			(cartItem) =>
				cartItem.item.code === code && cartItem.item.shade === currentShadeName
		);
	};

	// --- Clear Filters ---
	const clearFilters = () => {
		setSelectedGroup("All");
		setSelectedColor("All");
		setSelectedPenetration("All");
	};
	const hasActiveFilters =
		selectedGroup !== "All" ||
		selectedColor !== "All" ||
		selectedPenetration !== "All";

	// --- Render Components ---
	// --- Render Components ---

	// Filters UI Component (Revised based on image)
	const FilterSection = () => (
		<div className="space-y-8">
			{" "}
			{/* Increased spacing between sections */}
			{/* --- 1. Shade Type --- */}
			<fieldset>
				<legend className="font-semibold text-gray-800 text-lg mb-4 flex items-center gap-2">
					{/* Optional Icon instead of number? <Type size={20}/> */}
					<span className="text-primary font-bold">1.</span> Select Shade Type
				</legend>
				<div className="grid grid-cols-3 gap-3 md:gap-4">
					{[
						// Using provided descriptions
						{
							type: "roller",
							label: "Roller Shades",
							desc: "Sleek & versatile",
						},
						{
							type: "banded",
							label: "Zebra Shades",
							desc: "Modern light control",
						},
						{ type: "sheer", label: "Sheer Shades", desc: "Soft & charming" },
					].map(({ type, label, desc }) => (
						<motion.button
							key={type}
							onClick={() => setSelectedShade(type)}
							whileHover={{ y: -2 }}
							whileTap={{ scale: 0.98 }}
							transition={{ duration: 0.15 }}
							className={`pb-3 border-2 rounded-xl transition-all bg-white text-center ${
								// Use a more prominent border for selected state, closer to the image
								selectedShade === type
									? "border-primary shadow-md"
									: "border-gray-200 hover:border-gray-400"
							}`}
							disabled={loading}
						>
							{/* Image Container */}
							<div className="rounded-lg overflow-hidden mb-2 bg-gray-100 border border-gray-200">
								<img
									src={`/images/fabricSwatch/${type}_shade_swatches_v1.png`}
									alt={label}
									className="w-full h-full object-contain" // Use contain if icons aren't filling
								/>
							</div>
							{/* Text Content */}
							<p className="text-sm font-semibold text-gray-800 leading-tight">
								{label}
							</p>
							<p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
								{desc}
							</p>
						</motion.button>
					))}
				</div>
			</fieldset>
			{/* --- 2. Transparency --- */}
			<fieldset>
				<legend className="font-semibold text-gray-800 text-lg mb-4 flex items-center gap-2">
					<Layers size={18} className="text-primary opacity-80" /> {/* Icon */}
					<span className="text-primary font-bold">2.</span> Filter by
					Transparency
				</legend>
				<div className="flex flex-wrap gap-2">
					{/* Use pill shape buttons as per image */}
					{["All", ...allGroups].map((group) => (
						<motion.button
							key={group}
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
							onClick={() => setSelectedGroup(group)}
							disabled={loading}
							className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
								// Solid background for selected state
								selectedGroup === group
									? "bg-primary text-white border-primary"
									: "bg-white border-gray-300 hover:bg-gray-100 hover:border-gray-400"
							}`}
						>
							{group}
						</motion.button>
					))}
				</div>
			</fieldset>
			{/* --- 3. Color Family --- */}
			<fieldset>
				<legend className="font-semibold text-gray-800 text-lg mb-4 flex items-center gap-2">
					<Palette size={18} className="text-primary opacity-80" /> {/* Icon */}
					<span className="text-primary font-bold">3.</span> Filter by Colour
					Family
				</legend>
				{/* Grid layout matching the image (e.g., 6 columns) */}
				<div className="grid grid-cols-5 xs:grid-cols-6 gap-2 sm:gap-3">
					{/* Render 'All Colours' first */}
					<motion.button
						key="All-Color"
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
						onClick={() => setSelectedColor("All")}
						disabled={loading}
						className={`p-1 border-2 rounded-md transition-all text-center flex flex-col items-center ${
							// Use border style for selection matching the image
							selectedColor === "All"
								? "border-primary"
								: "border-gray-200 hover:border-gray-400 bg-white"
						}`}
						title="All Colours"
					>
						{/* Swatch Visual (Gradient or Icon) */}
						<div className="w-full h-8 sm:h-9 bg-gradient-to-r from-gray-100 via-white to-gray-200 rounded mb-1 border border-gray-200"></div>
						{/* Text Label Below */}
						<span className="text-[10px] sm:text-xs font-medium block leading-tight mt-auto text-gray-700">
							All Colours
						</span>
					</motion.button>

					{/* Render actual colors */}
					{allColours
						.filter((c) => c !== "All")
						.map((color) => (
							<motion.button
								key={color}
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
								onClick={() => setSelectedColor(color)}
								disabled={loading}
								className={`p-1 border-2 rounded-md transition-all text-center flex flex-col items-center ${
									// Use border style for selection
									selectedColor === color
										? "border-primary"
										: "border-gray-200 hover:border-gray-400 bg-white"
								}`}
								title={color}
							>
								{/* Swatch Image */}
								<div className="w-full h-8 sm:h-9 rounded mb-1 overflow-hidden bg-gray-100 border border-gray-200">
									<img
										src={`/images/fabricSwatch/${color.toLowerCase()}.jpg`}
										alt={color}
										className="w-full h-full object-cover" // Use object-cover
										loading="lazy" // Add lazy loading
										onError={(e) => {
											e.target.style.backgroundColor = "#f0f0f0";
											e.target.alt = `Missing image for ${color}`;
										}} // Handle missing images
									/>
								</div>
								{/* Text Label Below */}
								<span className="text-[10px] sm:text-xs font-medium block truncate leading-tight mt-auto text-gray-700">
									{color}
								</span>
							</motion.button>
						))}
				</div>
			</fieldset>
			{/* --- 4. Penetration Filter (Conditional) --- */}
			{selectedGroup === "Sunscreen" && (
				<fieldset className="pt-2">
					{" "}
					{/* Reduced top padding */}
					<legend className="font-semibold text-gray-800 text-lg mb-4 flex items-center gap-2">
						<Percent size={18} className="text-primary opacity-80" />{" "}
						{/* Icon */}
						<span className="text-primary font-bold">4.</span> Filter Sunscreen
						Openness
					</legend>
					<p className="text-xs text-gray-500 mb-3">
						Lower % blocks more light/heat, higher % allows more view-through.
					</p>
					<div className="flex flex-wrap gap-2">
						{/* Use pill buttons like Transparency */}
						{["All", "1%", "3%", "5%", "8%"].map((penetration) => (
							<motion.button
								key={penetration}
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
								onClick={() => setSelectedPenetration(penetration)}
								disabled={loading}
								className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
									selectedPenetration === penetration
										? "bg-primary text-white border-primary"
										: "bg-white border-gray-300 hover:bg-gray-100 hover:border-gray-400"
								}`}
							>
								{penetration === "All" ? "All" : `${penetration} Openness`}
							</motion.button>
						))}
					</div>
				</fieldset>
			)}
			{/* --- Clear Filters Button --- */}
			{hasActiveFilters && (
				<div className="pt-4 mt-6 border-t">
					<button
						onClick={clearFilters}
						className="text-xs text-blue-600 hover:underline"
						disabled={loading}
					>
						Clear All Filters (Show All Fabrics)
					</button>
				</div>
			)}
		</div>
	);
	// Cart Preview (More informative and actionable)
	const CartPreview = () => (
		<div className="fixed bottom-0 left-0 right-0 md:left-auto md:right-4 md:bottom-4 md:w-80 lg:w-96 bg-white/95 backdrop-blur-sm rounded-t-lg md:rounded-lg shadow-lg border border-gray-200 z-50 pb-[env(safe-area-inset-bottom)]">
			<div className="p-3 border-b border-gray-200 flex items-center justify-between">
				<div>
					<span className="font-semibold text-gray-800">Your Free Samples</span>
					<span
						className={`ml-2 text-sm font-bold ${
							fabricCart.length >= SHADE_LIMIT ? "text-red-600" : "text-primary"
						}`}
					>
						({fabricCart.length}/{SHADE_LIMIT})
					</span>
				</div>
				<Link
					to="/swatchcheckout"
					className={`px-4 py-1 rounded-lg text-sm font-medium bg-primary text-white transition-opacity ${
						fabricCart.length === 0
							? "opacity-50 cursor-not-allowed pointer-events-none"
							: "hover:bg-primary/90"
					}`}
					aria-disabled={fabricCart.length === 0}
				>
					Checkout Samples
				</Link>
			</div>
			<div
				className={`max-h-40 overflow-y-auto p-3 ${
					fabricCart.length === 0 ? "flex items-center justify-center" : ""
				}`}
			>
				{fabricCart.length === 0 ? (
					<p className="text-center text-xs text-gray-500 py-4">
						Add up to {SHADE_LIMIT} samples by clicking "Add Free Sample" on the
						fabrics below.
					</p>
				) : (
					<div className="grid grid-cols-5 gap-2">
						{" "}
						{/* Even more columns for smaller previews */}
						{fabricCart.map((cartItem, index) => {
							/* ... Cart item rendering with remove button ... */
							const item = cartItem.item;
							return (
								<div
									key={`${item.code}-${index}`}
									className="relative group aspect-square"
									title={`${item.ocs_name} - ${item.ocs_colour_name}`}
								>
									<img
										src={item.image}
										alt={item.ocs_colour_name || item.name}
										className="w-full h-full object-cover rounded-md border border-gray-100"
									/>
									<button
										onClick={() => handleRemoveFromCart(item)}
										className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-md flex items-center justify-center border-2 border-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
										aria-label={`Remove sample ${
											item.ocs_colour_name || item.name
										}`}
									>
										{" "}
										<Trash2 size={10} strokeWidth={3} />{" "}
									</button>
								</div>
							);
						})}
						{/* Fill remaining slots visually */}
						{[...Array(Math.max(0, SHADE_LIMIT - fabricCart.length))].map(
							(_, i) => (
								<div
									key={`placeholder-${i}`}
									className="aspect-square rounded-md border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center"
								>
									<span className="text-gray-300 text-xl font-light">
										{fabricCart.length + i + 1}
									</span>
								</div>
							)
						)}
					</div>
				)}
			</div>
		</div>
	);

	// --- Main Page Render ---
	return (
		<div className="min-h-screen bg-gray-100 pb-48 md:pb-24">
			{" "}
			{/* Increased padding-bottom */}
			{/* --- Header Section --- */}
			<section className="pt-16 pb-10 md:pt-24 md:pb-16 bg-gradient-to-b from-white to-gray-100 text-center">
				{/* ... (Header content remains largely the same, focusing on "Free Samples") ... */}
				<div className="container mx-auto px-4">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, ease: "easeOut" }}
					>
						<span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4 tracking-wide uppercase">
							Try Before You Buy
						</span>
						<h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
							Find Your Perfect Fabric
						</h1>
						<p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
							See and feel the quality firsthand. Choose up to{" "}
							<strong className="text-primary">
								{SHADE_LIMIT} free samples
							</strong>{" "}
							delivered right to your door to ensure the perfect match for your
							space.
						</p>
					</motion.div>
				</div>
			</section>
			{/* --- Main Content Area (Filters + Grid) --- */}
			<div className="container mx-auto px-4 py-8">
				<div className="grid grid-cols-1 md:grid-cols-[280px_1fr] lg:grid-cols-[320px_1fr] gap-6 md:gap-8">
					{/* --- Filters Sidebar --- */}
					<AnimatePresence>
						{(showFilters || !isMobile) && (
							<motion.div
								key="filters-panel"
								className={`bg-white rounded-xl border border-gray-200 shadow-sm p-5 ${
									isMobile
										? "mb-6"
										: "sticky top-6 self-start h-[calc(100vh-3rem)] overflow-y-auto"
								}`}
							>
								{" "}
								{/* Sticky & Scrollable Sidebar */}
								<div className="flex justify-between items-center mb-4 pb-3 border-b md:border-none">
									<h2 className="text-lg font-semibold">Filter Fabrics</h2>
									{isMobile && (
										<button
											onClick={() => setShowFilters(false)}
											className="text-gray-500 hover:text-gray-700"
										>
											{" "}
											<X size={20} />{" "}
										</button>
									)}
								</div>
								<FilterSection />
							</motion.div>
						)}
					</AnimatePresence>

					{/* --- Fabric Grid Area --- */}
					<div className="w-full min-h-[70vh]">
						{/* Filter Toggle Button (Mobile) */}
						<div className="md:hidden mb-4">
							<button
								onClick={() => setShowFilters(!showFilters)}
								className="w-full px-4 py-2.5 bg-white rounded-lg border border-gray-300 flex items-center justify-between text-sm font-medium shadow-sm"
							>
								{showFilters
									? "Hide Filters"
									: `Show Filters ${hasActiveFilters ? " (...)" : ""}`}{" "}
								<FilterIcon size={18} className="text-gray-600" />
							</button>
						</div>
						{/* Results Count & Help Text */}
						{!loading && !error && (
							<div className="mb-4 text-sm text-gray-600">
								Showing {Object.values(groupedFilteredFabrics).flat().length} of{" "}
								{allRawFabrics.length} fabrics. Click "Add Free Sample" below
								(limit {SHADE_LIMIT}).
							</div>
						)}
						{/* Grid Content */}
						<AnimatePresence mode="wait">
							{loading ? (
								<motion.div key="loading" /* ... */>
									{" "}
									<LoadingSpinner text="Loading Fabrics..." />{" "}
								</motion.div>
							) : error ? (
								<motion.div
									key="error"
									/* ... Error message ... */ className="text-center py-12 text-red-600 bg-red-50 p-6 rounded-lg border border-red-200"
								>
									{" "}
									<p className="font-medium">{error}</p>{" "}
									<button
										onClick={() => loadFabricDataForShade(selectedShade)}
										className="mt-4 text-sm text-primary hover:underline"
									>
										Try Again
									</button>{" "}
								</motion.div>
							) : Object.keys(groupedFilteredFabrics).length === 0 ? (
								<motion.div
									key="no-results"
									/* ... No results message ... */ className="text-center py-16 text-gray-500 bg-white rounded-lg border border-gray-200 shadow-sm"
								>
									{" "}
									<p className="font-medium text-lg">No Fabrics Found</p>{" "}
									<p className="text-sm mt-2">
										Try adjusting the filters or selecting a different shade
										type.
									</p>{" "}
									{hasActiveFilters && (
										<button
											onClick={clearFilters}
											className="mt-4 text-xs text-blue-600 hover:underline"
										>
											Clear Filters
										</button>
									)}{" "}
								</motion.div>
							) : (
								<motion.div
									key="fabric-results"
									/* ... */ className="space-y-6 md:space-y-8"
								>
									{Object.entries(groupedFilteredFabrics).map(
										([series, items]) => (
											<div
												key={series}
												className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
											>
												{/* Series Header */}
												<div className="p-4 border-b border-gray-200">
													<FabricSeriesHeader
														title={series}
														headerInfo={{
															group: items[0]?.group
																? items[0].group.charAt(0).toUpperCase() +
																  items[0].group.slice(1)
																: "",
															composition:
																items[0]?.composition /* other info */,
														}}
													/>
												</div>
												{/* Swatch Grid */}
												<div className="p-4">
													<div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
														{items.map((item, idx) => {
															const inCart = existsInCart(item.code);
															return (
																<div
																	key={`${item.code}-${idx}`}
																	className="group flex flex-col items-center"
																>
																	{/* Image */}
																	<div className="relative w-full aspect-square mb-2 rounded-lg overflow-hidden border border-gray-200 shadow-sm cursor-pointer bg-gray-50">
																		<ImageZoomWithButton
																			item={item}
																			isFabricSelected={false}
																			customize={false}
																			hideButton={true}
																		/>
																	</div>
																	{/* Name */}
																	<p
																		className="text-xs sm:text-sm font-medium text-center text-gray-800 mb-2 flex-grow truncate w-full px-1"
																		title={item.ocs_colour_name}
																	>
																		{" "}
																		{item.ocs_colour_name}{" "}
																	</p>
																	{/* Add/Remove Button */}
																	<motion.button
																		whileTap={{ scale: 0.95 }}
																		onClick={() =>
																			inCart
																				? handleRemoveFromCart(item)
																				: handleAddToCart(item)
																		}
																		disabled={
																			!inCart &&
																			fabricCart.length >= SHADE_LIMIT
																		}
																		className={`w-full py-1.5 text-[11px] sm:text-xs font-semibold rounded-md transition-all duration-150 flex items-center justify-center gap-1 ${
																			inCart
																				? "bg-red-100 text-red-700 hover:bg-red-200"
																				: fabricCart.length >= SHADE_LIMIT
																				? "bg-gray-200 text-gray-500 cursor-not-allowed"
																				: "bg-primary/10 text-primary hover:bg-primary/20"
																		}`}
																		aria-label={
																			inCart
																				? `Remove sample ${item.ocs_colour_name}`
																				: `Add free sample ${item.ocs_colour_name}`
																		}
																	>
																		{inCart ? (
																			<>
																				<Trash2 size={12} /> Remove
																			</>
																		) : fabricCart.length >= SHADE_LIMIT ? (
																			"Limit Reached"
																		) : (
																			<>
																				<Check size={12} /> Add Sample
																			</>
																		)}
																	</motion.button>
																</div>
															);
														})}
													</div>
												</div>
											</div>
										)
									)}
								</motion.div>
							)}
						</AnimatePresence>
					</div>
				</div>
			</div>
			{/* --- Cart Preview --- */}
			<CartPreview />
			{/* --- Toast --- */}
			<AnimatePresence>
				{" "}
				{toast && (
					<Toast
						show={!!toast}
						type={toast.type}
						message={toast.message}
						onClose={() => setToast(null)}
						duration={2500}
					/>
				)}{" "}
			</AnimatePresence>
		</div>
	);
};

export default FabricSwatch;
